import type React from "react";
import {
    Building2,
    CalendarArrowUpIcon,
    CalendarClockIcon,
    CalendarOff,
    CalendarX2,
    CalendarXIcon,
    ChartLineIcon,
    CircleDollarSignIcon,
    DollarSignIcon,
    HashIcon,
    Percent,
    TrendingDown,
} from "lucide-react";

import StatsCard from "@/lib/shadcn/components/stats-card";
import DonutChartCard, { DonutChartData } from "@/lib/shadcn/components/donut-chart-card";
import { overViewBarChartIncomeFormatter } from "@/utils/formatters";
import type { BookingPaceStats } from "@/store/bookingPaceSlice";
import type { SectionDef, TileDef } from "@/lib/dashboard-grid/types";
import { bookingPaceTooltips } from "@/pages/dashboards/booking-pace-tooltips";

/**
 * Booking Pace tile registry/factory — maps the page's cards/charts into the
 * generic dashboard-grid tile model. Carries no behaviour of its own. The Default
 * arrangement MUST reproduce the current card order/grouping/spans (FR-24) — it is
 * the visual baseline for the editor.
 *
 * Traceability: FR-24 and the tile/grid model are dashboard-layout-editor
 * (ADR-022). The card CONTENT spans features: the five cancellation cards are
 * booking-pace-cancellations (ADR-019); the two ratio cards + Bookings Per Property
 * are booking-pace-extra-metrics (ADR-020). Inline refs are qualified accordingly.
 *
 * Design note: each cancellation card becomes its own TileDef so it is individually
 * draggable. Every card reads ONLY fields already on the section's BookingPaceStats
 * slice (cancelledBookings, cancelledDays, daysBooked, bookingsReceived) and
 * recomputes its own value via the ratio / pct helpers, so no per-card
 * accessor is needed — the factory is parameterised by section-level data, not a
 * card lookup table. `render` ignores TileRenderCtx (types.ts leaves ctx open
 * precisely so these tiles can close over their stats instead).
 */

/**
 * The ONE guarded division every rate card on this page goes through. `> 0`, not
 * `!== 0`: a period length is a subtraction and can come out negative if the range
 * ends before it starts, and a negative denominator yields a quietly-wrong negative
 * rate rather than an obvious infinity.
 *
 * Do NOT "guard" a division with a trailing `|| 0` — `Infinity` is truthy, so it
 * only catches the harmless 0/0 = NaN case and lets `n/0` render as "Infinity".
 * That was the live Average-Daily-Booking-Rate bug on a single-day period
 * (.verified/codebase/CONCERNS.md, div-by-zero).
 */
const ratio = (numerator: number, denominator: number): number => (denominator > 0 ? numerator / denominator : 0);

const MILLIS_PER_DAY = 1000 * 60 * 60 * 24;

/**
 * The `YYYY-MM-DD` a window end is sent to the API as — a UTC calendar day. Both pace
 * pages build their `startDate`/`endDate` query params with this, and
 * `inclusivePeriodDays` counts the SAME strings, so the denominator cannot drift from
 * the window the backend actually aggregated over.
 */
export const apiWindowDay = (date: Date): string => date.toISOString().split("T")[0];

/** Index of a window end's UTC calendar day — whole days since the epoch. */
const utcDayIndex = (date: Date): number => Date.parse(apiWindowDay(date)) / MILLIS_PER_DAY;

/**
 * UTC calendar days in the requested window, counted INCLUSIVELY — the denominator of
 * every "per day in the period" tile (Average Daily Booking Rate, Average Daily
 * Cancellation Rate).
 *
 * Inclusive, because both the range the user reads ("2026-04-05 - 2026-04-05" is one
 * day to them) and the window the backend aggregates over (`booking_date BETWEEN
 * start AND end`) include the end date. A plain end-minus-start is therefore short by
 * one day for EVERY period, and zero for a single-day one — which turned a visible
 * Infinity into a plausible-looking 0.0 once the division was guarded.
 *
 * Counted from the UTC calendar DAYS, not from elapsed milliseconds, because the two
 * ends routinely have DIFFERENT times of day and elapsed-millis then contradicts the
 * requested window: the slice starts out holding `new Date()` and `now - 10d` (raw
 * instants), while a date input writes UTC midnight, so picking ONE end leaves a mixed
 * pair. Loaded 18:00 and picking start 2026-08-01 asked the API for 08-01..08-17 — 17
 * days — but 16.75 elapsed days rounded to 17 and the tile divided by 18. Day indices
 * make the count exact for any pair of instants, so there is nothing left to round.
 *
 * An inverted range (end before start) still returns <= 0, which is exactly what
 * `ratio`'s guard is for.
 */
export const inclusivePeriodDays = (startDate: Date, endDate: Date): number => utcDayIndex(endDate) - utcDayIndex(startDate) + 1;

const pct = (n: number, d: number): number => ratio(n, d) * 100;

/**
 * Default spans (12-col grid), ui-spec "Tile defaults". `h` is in DashboardGrid
 * ROW_HEIGHT units (90px + 10px margin), sized so each slot contains its card with
 * no overflow: stat h:2 ≈ 190px (tallest stat card), chart h:4 ≈ 390px (donut
 * header ~80px + 200px pie + py-6, with headroom so the fixed pie never clips).
 */
const STAT_LAYOUT = { w: 4, h: 2, minW: 2 } as const; // 3-up stat row
const VELOCITY_LAYOUT = { w: 3, h: 2, minW: 2 } as const; // 4-up velocity row
const CHART_LAYOUT = { w: 6, h: 4, minW: 4 } as const; // side-by-side, taller

/**
 * Single `TileDef` factory for the stat cards — id `<sectionId>:<idSuffix>`, type
 * "stat", `STAT_LAYOUT` by default (callers pass `VELOCITY_LAYOUT` for the 4-up
 * velocity row). Module-level so all three tile groups share ONE definition.
 */
const makeStat = (
    sectionId: string,
    idSuffix: string,
    title: string,
    node: React.ReactNode,
    layout: { w: number; h: number; minW: number } = STAT_LAYOUT,
): TileDef => ({
    id: `${sectionId}:${idSuffix}`,
    title,
    section: sectionId,
    type: "stat",
    defaultLayout: layout,
    render: () => node,
});

/**
 * Slugify a booking-pace map key ("All Sources", "Booking.com") into a stable,
 * url/test-id-safe section id ("all-sources", "booking-com"). The section ORDER is
 * driven by the caller (grossIncome desc, "All Sources" forced first) — this only
 * derives the id.
 */
export const sectionIdForKey = (key: string): string =>
    key === "All Sources"
        ? "all-sources"
        : key
              .toLowerCase()
              .replace(/[^a-z0-9]+/g, "-")
              .replace(/^-+|-+$/g, "");

/** Each column's own window length, so a per-day tile never mixes columns. */
interface PeriodDays {
    current: number;
    prev: number;
    prevYear: number;
}

/** Per-section stats slice — exactly what the page already computes per accordion item. */
export interface SectionStats {
    /** The raw map key ("All Sources", "Airbnb", "Booking.com", …). */
    key: string;
    stats: BookingPaceStats | undefined;
    prev: BookingPaceStats | undefined;
    prevYear: BookingPaceStats | undefined;
}

/** Everything the registry factory needs — the same values the page threads into its cards. */
export interface BookingPaceTilesInput {
    /** Sections in render order; "All Sources" first, then platforms by grossIncome desc. */
    sectionsData: SectionStats[];
    /**
     * Day count of each window, one per comparison column. The comparison windows are
     * independently editable inputs, so they can be any length — a per-day value must
     * divide by ITS OWN window's days or it silently rescales by the length difference
     * (a 10-day previous period over the current 31 days read 3.2 instead of 10.0).
     */
    numberOfDays: number;
    numberOfDaysPrev: number;
    numberOfDaysPrevYear: number;
    /** Formatted "start - end" period string shown on every card. */
    period: string;
    /** Operational-property counts for Bookings Per Property (All Sources only). */
    opCountCurrent: number;
    opCountPrev: number;
    opCountPrevYear: number;
    /** The full stats map — the two pies aggregate across every platform. */
    allStats: Map<string, BookingPaceStats>;
}

/** Build the donut data for a pie, excluding the "All Sources" aggregate row. */
const preparePieChartData = (
    stats: Map<string, BookingPaceStats>,
    key: keyof BookingPaceStats,
    valueFormatter?: (val: number) => string,
): DonutChartData[] =>
    Array.from(stats.entries())
        .filter(([sourceName]) => sourceName !== "All Sources")
        .map(([sourceName, sourceStats]) => ({ name: sourceName, value: sourceStats[key], valueFormatter }))
        .sort((a, b) => b.value - a.value);

/**
 * The cancellation group as FIVE individual stat TileDefs, each independently
 * draggable/hideable. Two counts (Bookings/Days Cancelled) +
 * Average-Daily-Cancellation-Rate are booking-pace-cancellations; the two ratio
 * cards (Cancellation Rate, Booking Cancellation Rate) are
 * booking-pace-extra-metrics. Returned in the same consecutive order as the old
 * composite so the Default arrangement renders them adjacent (FR-24,
 * dashboard-layout-editor).
 */
const cancellationTiles = (sectionId: string, s: SectionStats, days: PeriodDays, period: string): TileDef[] => {
    const { stats, prev, prevYear } = s;
    const stat = (idSuffix: string, title: string, node: React.ReactNode): TileDef => makeStat(sectionId, idSuffix, title, node);
    return [
        stat(
            "bookings-cancelled",
            "Bookings Cancelled",
            <StatsCard
                title="Bookings Cancelled"
                value={stats?.cancelledBookings || 0}
                lasPeriodValue={prev?.cancelledBookings || 0}
                lastYearValue={prevYear?.cancelledBookings || 0}
                periodDescription={period}
                CardIcon={CalendarX2}
                iconVariant="red"
                className="h-full"
                dense
                tooltip={bookingPaceTooltips["Bookings Cancelled"]}
            />,
        ),
        stat(
            "days-cancelled",
            "Days Cancelled",
            <StatsCard
                title="Days Cancelled"
                value={stats?.cancelledDays || 0}
                lasPeriodValue={prev?.cancelledDays || 0}
                lastYearValue={prevYear?.cancelledDays || 0}
                periodDescription={period}
                CardIcon={CalendarOff}
                iconVariant="red"
                className="h-full"
                dense
                tooltip={bookingPaceTooltips["Days Cancelled"]}
            />,
        ),
        stat(
            "average-daily-cancellation-rate",
            "Average Daily Cancellation Rate",
            <StatsCard
                title="Average Daily Cancellation Rate"
                value={ratio(stats?.cancelledDays || 0, days.current)}
                lasPeriodValue={ratio(prev?.cancelledDays || 0, days.prev)}
                lastYearValue={ratio(prevYear?.cancelledDays || 0, days.prevYear)}
                valueFormatter={(value) => value.toFixed(1)}
                periodDescription={period}
                CardIcon={TrendingDown}
                iconVariant="red"
                className="h-full"
                dense
                tooltip={bookingPaceTooltips["Average Daily Cancellation Rate"]}
            />,
        ),
        stat(
            "cancellation-rate",
            "Cancellation Rate",
            <StatsCard
                title="Cancellation Rate"
                value={pct(stats?.cancelledDays || 0, stats?.daysBooked || 0)}
                lasPeriodValue={pct(prev?.cancelledDays || 0, prev?.daysBooked || 0)}
                lastYearValue={pct(prevYear?.cancelledDays || 0, prevYear?.daysBooked || 0)}
                valueFormatter={(v) => `${v.toFixed(0)}%`}
                periodDescription={period}
                CardIcon={Percent}
                iconVariant="red"
                className="h-full"
                dense
                data-testid="cancellation-rate-card"
                tooltip={bookingPaceTooltips["Cancellation Rate"]}
            />,
        ),
        stat(
            "booking-cancellation-rate",
            "Booking Cancellation Rate",
            <StatsCard
                title="Booking Cancellation Rate"
                value={pct(stats?.cancelledBookings || 0, stats?.bookingsReceived || 0)}
                lasPeriodValue={pct(prev?.cancelledBookings || 0, prev?.bookingsReceived || 0)}
                lastYearValue={pct(prevYear?.cancelledBookings || 0, prevYear?.bookingsReceived || 0)}
                valueFormatter={(v) => `${v.toFixed(0)}%`}
                periodDescription={period}
                CardIcon={Percent}
                iconVariant="red"
                className="h-full"
                dense
                data-testid="booking-cancellation-rate-card"
                tooltip={bookingPaceTooltips["Booking Cancellation Rate"]}
            />,
        ),
    ];
};

/** All Sources tiles, in the page's current visual order. */
const allSourcesTiles = (sectionId: string, input: BookingPaceTilesInput): TileDef[] => {
    const s = input.sectionsData[0];
    const { stats, prev, prevYear } = s;
    const { period, opCountCurrent, opCountPrev, opCountPrevYear, allStats } = input;
    const days: PeriodDays = { current: input.numberOfDays, prev: input.numberOfDaysPrev, prevYear: input.numberOfDaysPrevYear };

    const stat = (
        idSuffix: string,
        title: string,
        node: React.ReactNode,
        layout: { w: number; h: number; minW: number } = STAT_LAYOUT,
    ): TileDef => makeStat(sectionId, idSuffix, title, node, layout);

    const tiles: TileDef[] = [
        // Top row — main stats (3-up).
        stat(
            "gross-income",
            "Gross Income",
            <StatsCard
                title="Gross Income"
                value={stats?.grossIncome || 0}
                lasPeriodValue={prev?.grossIncome || 0}
                lastYearValue={prevYear?.grossIncome || 0}
                periodDescription={period}
                valueFormatter={overViewBarChartIncomeFormatter}
                CardIcon={DollarSignIcon}
                iconVariant="green"
                iconBgClassName="bg-green-200"
                className="h-full"
                dense
                tooltip={bookingPaceTooltips["Gross Income"]}
            />,
        ),
        stat(
            "days-booked",
            "Days Booked",
            <StatsCard
                title="Days Booked"
                value={stats?.daysBooked || 0}
                lasPeriodValue={prev?.daysBooked || 0}
                lastYearValue={prevYear?.daysBooked || 0}
                periodDescription={period}
                iconVariant="orange"
                iconBgClassName="bg-orange-200"
                className="h-full"
                dense
                tooltip={bookingPaceTooltips["Days Booked"]}
            />,
        ),
        stat(
            "bookings-received",
            "Bookings Received",
            <StatsCard
                title="Bookings Received"
                value={stats?.bookingsReceived || 0}
                lasPeriodValue={prev?.bookingsReceived || 0}
                lastYearValue={prevYear?.bookingsReceived || 0}
                periodDescription={period}
                CardIcon={HashIcon}
                iconVariant="blue"
                iconBgClassName="bg-blue-200"
                className="h-full"
                dense
                tooltip={bookingPaceTooltips["Bookings Received"]}
            />,
        ),
        // Cancellation row (decomposed, 5 cards).
        ...cancellationTiles(sectionId, s, days, period),
        // Breakdown pies — side by side, taller.
        {
            id: `${sectionId}:income-breakdown-pie`,
            title: "Gross Income Breakdown",
            section: sectionId,
            type: "chart",
            defaultLayout: CHART_LAYOUT,
            render: () => (
                <DonutChartCard
                    data={preparePieChartData(allStats, "grossIncome", overViewBarChartIncomeFormatter)}
                    title="Gross Income Breakdown"
                    subtitle={""}
                    height={200}
                    className="h-full"
                />
            ),
        },
        {
            id: `${sectionId}:days-breakdown-pie`,
            title: "Days Booked Breakdown",
            section: sectionId,
            type: "chart",
            defaultLayout: CHART_LAYOUT,
            render: () => (
                <DonutChartCard
                    className="h-full"
                    data={preparePieChartData(allStats, "daysBooked")}
                    title="Days Booked Breakdown"
                    subtitle={""}
                    height={200}
                />
            ),
        },
        // Velocity row (4-up).
        stat(
            "average-daily-booking-rate",
            "Average Daily Booking Rate",
            <StatsCard
                title="Average Daily Booking Rate"
                value={ratio(stats?.daysBooked || 0, days.current)}
                lasPeriodValue={ratio(prev?.daysBooked || 0, days.prev)}
                lastYearValue={ratio(prevYear?.daysBooked || 0, days.prevYear)}
                valueFormatter={(value) => value.toFixed(1)}
                periodDescription={period}
                CardIcon={CalendarClockIcon}
                iconVariant="blue"
                iconBgClassName="bg-blue-200"
                className="h-full"
                dense
                tooltip={bookingPaceTooltips["Average Daily Booking Rate"]}
            />,
            VELOCITY_LAYOUT,
        ),
        stat(
            "future-days-booked",
            "Future Days Booked",
            <StatsCard
                title="Future Days Booked"
                value={stats?.futureDaysBooked || 0}
                lasPeriodValue={prev?.futureDaysBooked || 0}
                lastYearValue={prevYear?.futureDaysBooked || 0}
                periodDescription={period}
                CardIcon={CalendarArrowUpIcon}
                iconVariant="primary"
                iconBgClassName="bg-gray-600"
                className="h-full"
                dense
                tooltip={bookingPaceTooltips["Future Days Booked"]}
            />,
            VELOCITY_LAYOUT,
        ),
        stat(
            "current-month-days-booked",
            "Current Month - Days Booked",
            <StatsCard
                title="Current Month - Days Booked"
                value={(stats?.daysBooked || 0) - (stats?.futureDaysBooked || 0)}
                lasPeriodValue={(prev?.daysBooked || 0) - (prev?.futureDaysBooked || 0)}
                lastYearValue={(prevYear?.daysBooked || 0) - (prevYear?.futureDaysBooked || 0)}
                periodDescription={period}
                CardIcon={CalendarXIcon}
                iconVariant="primary"
                iconBgClassName="bg-gray-600"
                className="h-full"
                dense
                tooltip={bookingPaceTooltips["Current Month - Days Booked"]}
            />,
            VELOCITY_LAYOUT,
        ),
        stat(
            "bookings-per-property",
            "Bookings Per Property",
            <StatsCard
                title="Bookings Per Property"
                value={ratio(stats?.bookingsReceived || 0, opCountCurrent)}
                lasPeriodValue={ratio(prev?.bookingsReceived || 0, opCountPrev)}
                lastYearValue={ratio(prevYear?.bookingsReceived || 0, opCountPrevYear)}
                valueFormatter={(v) => v.toFixed(1)}
                periodDescription={period}
                CardIcon={Building2}
                iconVariant="blue"
                iconBgClassName="bg-blue-200"
                className="h-full"
                dense
                data-testid="bookings-per-property-card"
                tooltip={bookingPaceTooltips["Bookings Per Property"]}
            />,
            VELOCITY_LAYOUT,
        ),
        // Bottom row 1 — current income/adr (3-up).
        stat(
            "current-net-income",
            "Current Net Income",
            <StatsCard
                title="Current Net Income"
                value={stats?.currentNetIncome || 0}
                lasPeriodValue={prev?.currentNetIncome || 0}
                lastYearValue={prevYear?.currentNetIncome || 0}
                valueFormatter={overViewBarChartIncomeFormatter}
                periodDescription={period}
                CardIcon={CircleDollarSignIcon}
                iconVariant="green"
                iconBgClassName="bg-green-200"
                className="h-full"
                dense
                tooltip={bookingPaceTooltips["Current Net Income"]}
            />,
        ),
        stat(
            "current-gross-adr",
            "Current Gross Adr",
            <StatsCard
                title="Current Gross Adr"
                value={stats?.currentGrossAdr || 0}
                lasPeriodValue={prev?.currentGrossAdr || 0}
                lastYearValue={prevYear?.currentGrossAdr || 0}
                periodDescription={period}
                CardIcon={ChartLineIcon}
                iconVariant="green"
                iconBgClassName="bg-green-200"
                className="h-full"
                dense
                tooltip={bookingPaceTooltips["Current Gross Adr"]}
            />,
        ),
        stat(
            "current-net-adr",
            "Current Net Adr",
            <StatsCard
                title="Current Net Adr"
                value={stats?.currentNetAdr || 0}
                lasPeriodValue={prev?.currentNetAdr || 0}
                lastYearValue={prevYear?.currentNetAdr || 0}
                periodDescription={period}
                CardIcon={ChartLineIcon}
                iconVariant="green"
                iconBgClassName="bg-green-200"
                className="h-full"
                dense
                tooltip={bookingPaceTooltips["Current Net Adr"]}
            />,
        ),
        // Bottom row 2 — future income/adr (3-up).
        stat(
            "future-net-income",
            "Future Net Income",
            <StatsCard
                title="Future Net Income"
                value={stats?.futureNetIncome || 0}
                lasPeriodValue={prev?.futureNetIncome || 0}
                lastYearValue={prevYear?.futureNetIncome || 0}
                valueFormatter={overViewBarChartIncomeFormatter}
                periodDescription={period}
                CardIcon={CircleDollarSignIcon}
                iconVariant="green"
                iconBgClassName="bg-green-200"
                className="h-full"
                dense
                tooltip={bookingPaceTooltips["Future Net Income"]}
            />,
        ),
        stat(
            "future-gross-adr",
            "Future Gross Adr",
            <StatsCard
                title="Future Gross Adr"
                value={stats?.futureGrossAdr || 0}
                lasPeriodValue={prev?.futureGrossAdr || 0}
                lastYearValue={prevYear?.futureGrossAdr || 0}
                periodDescription={period}
                CardIcon={ChartLineIcon}
                iconVariant="green"
                iconBgClassName="bg-green-200"
                className="h-full"
                dense
                tooltip={bookingPaceTooltips["Future Gross Adr"]}
            />,
        ),
        stat(
            "future-net-adr",
            "Future Net Adr",
            <StatsCard
                title="Future Net Adr"
                value={stats?.futureNetAdr || 0}
                lasPeriodValue={prev?.futureNetAdr || 0}
                lastYearValue={prevYear?.futureNetAdr || 0}
                periodDescription={period}
                CardIcon={ChartLineIcon}
                iconVariant="green"
                iconBgClassName="bg-green-200"
                className="h-full"
                dense
                tooltip={bookingPaceTooltips["Future Net Adr"]}
            />,
        ),
    ];

    return tiles;
};

/** Per-platform tiles, in the page's current visual order. */
const platformTiles = (sectionId: string, s: SectionStats, days: PeriodDays, period: string): TileDef[] => {
    const { stats, prev, prevYear } = s;

    const stat = (idSuffix: string, title: string, node: React.ReactNode): TileDef => makeStat(sectionId, idSuffix, title, node);

    return [
        stat(
            "gross-income-current-days",
            "Gross Income - Current Days",
            <StatsCard
                title="Gross Income - Current Days"
                value={stats?.grossIncome || 0}
                lasPeriodValue={prev?.grossIncome || 0}
                lastYearValue={prevYear?.grossIncome || 0}
                valueFormatter={overViewBarChartIncomeFormatter}
                periodDescription={period}
                CardIcon={DollarSignIcon}
                iconVariant="green"
                iconBgClassName="bg-green-200"
                className="h-full"
                dense
                tooltip={bookingPaceTooltips["Gross Income - Current Days"]}
            />,
        ),
        stat(
            "days-booked",
            "Days Booked",
            <StatsCard
                title="Days Booked"
                value={stats?.daysBooked || 0}
                lasPeriodValue={prev?.daysBooked || 0}
                lastYearValue={prevYear?.daysBooked || 0}
                periodDescription={period}
                iconVariant="orange"
                iconBgClassName="bg-orange-200"
                className="h-full"
                dense
                tooltip={bookingPaceTooltips["Days Booked"]}
            />,
        ),
        stat(
            "bookings-received",
            "Bookings Received",
            <StatsCard
                title="Bookings Received"
                value={stats?.bookingsReceived || 0}
                lasPeriodValue={prev?.bookingsReceived || 0}
                lastYearValue={prevYear?.bookingsReceived || 0}
                periodDescription={period}
                CardIcon={HashIcon}
                iconVariant="blue"
                iconBgClassName="bg-blue-200"
                className="h-full"
                dense
                tooltip={bookingPaceTooltips["Bookings Received"]}
            />,
        ),
        stat(
            "gross-income-future-days",
            "Gross Income - Future Days",
            <StatsCard
                title="Gross Income - Future Days"
                value={stats?.futureGrossIncome || 0}
                lasPeriodValue={prev?.futureGrossIncome || 0}
                lastYearValue={prevYear?.futureGrossIncome || 0}
                valueFormatter={overViewBarChartIncomeFormatter}
                periodDescription={period}
                CardIcon={DollarSignIcon}
                iconVariant="green"
                iconBgClassName="bg-green-200"
                className="h-full"
                dense
                tooltip={bookingPaceTooltips["Gross Income - Future Days"]}
            />,
        ),
        stat(
            "days-booked-current-days",
            "Days Booked - Current Days",
            <StatsCard
                title="Days Booked - Current Days"
                value={stats?.currentDaysBooked || 0}
                lasPeriodValue={prev?.currentDaysBooked || 0}
                lastYearValue={prevYear?.currentDaysBooked || 0}
                periodDescription={period}
                CardIcon={CalendarArrowUpIcon}
                iconVariant="primary"
                iconBgClassName="bg-gray-600"
                className="h-full"
                dense
                tooltip={bookingPaceTooltips["Days Booked - Current Days"]}
            />,
        ),
        stat(
            "days-booked-future-days",
            "Days Booked - Future Days",
            <StatsCard
                title="Days Booked - Future Days"
                value={stats?.futureDaysBooked || 0}
                lasPeriodValue={prev?.futureDaysBooked || 0}
                lastYearValue={prevYear?.futureDaysBooked || 0}
                periodDescription={period}
                CardIcon={CalendarArrowUpIcon}
                iconVariant="primary"
                iconBgClassName="bg-gray-600"
                className="h-full"
                dense
                tooltip={bookingPaceTooltips["Days Booked - Future Days"]}
            />,
        ),
        stat(
            "net-income-current-days",
            "Net Income - Current Days",
            <StatsCard
                title="Net Income - Current Days"
                value={stats?.currentNetIncome || 0}
                lasPeriodValue={prev?.currentNetIncome || 0}
                lastYearValue={prevYear?.currentNetIncome || 0}
                valueFormatter={overViewBarChartIncomeFormatter}
                periodDescription={period}
                CardIcon={CircleDollarSignIcon}
                iconVariant="green"
                iconBgClassName="bg-green-200"
                className="h-full"
                dense
                tooltip={bookingPaceTooltips["Net Income - Current Days"]}
            />,
        ),
        stat(
            "gross-adr-current-days",
            "Gross Adr - Current Days",
            <StatsCard
                title="Gross Adr - Current Days"
                value={stats?.currentGrossAdr || 0}
                lasPeriodValue={prev?.currentGrossAdr || 0}
                lastYearValue={prevYear?.currentGrossAdr || 0}
                periodDescription={period}
                CardIcon={ChartLineIcon}
                iconVariant="green"
                iconBgClassName="bg-green-200"
                className="h-full"
                dense
                tooltip={bookingPaceTooltips["Gross Adr - Current Days"]}
            />,
        ),
        stat(
            "net-adr-current-days",
            "Net Adr - Current Days",
            <StatsCard
                title="Net Adr - Current Days"
                value={stats?.currentNetAdr || 0}
                lasPeriodValue={prev?.currentNetAdr || 0}
                lastYearValue={prevYear?.currentNetAdr || 0}
                periodDescription={period}
                CardIcon={ChartLineIcon}
                iconVariant="green"
                iconBgClassName="bg-green-200"
                className="h-full"
                dense
                tooltip={bookingPaceTooltips["Net Adr - Current Days"]}
            />,
        ),
        stat(
            "net-income-future-days",
            "Net Income - Future Days",
            <StatsCard
                title="Net Income - Future Days"
                value={stats?.futureNetIncome || 0}
                lasPeriodValue={prev?.futureNetIncome || 0}
                lastYearValue={prevYear?.futureNetIncome || 0}
                valueFormatter={overViewBarChartIncomeFormatter}
                periodDescription={period}
                CardIcon={CircleDollarSignIcon}
                iconVariant="green"
                iconBgClassName="bg-green-200"
                className="h-full"
                dense
                tooltip={bookingPaceTooltips["Net Income - Future Days"]}
            />,
        ),
        stat(
            "gross-adr-future-days",
            "Gross Adr - Future Days",
            <StatsCard
                title="Gross Adr - Future Days"
                value={stats?.futureGrossAdr || 0}
                lasPeriodValue={prev?.futureGrossAdr || 0}
                lastYearValue={prevYear?.futureGrossAdr || 0}
                periodDescription={period}
                CardIcon={ChartLineIcon}
                iconVariant="green"
                iconBgClassName="bg-green-200"
                className="h-full"
                dense
                tooltip={bookingPaceTooltips["Gross Adr - Future Days"]}
            />,
        ),
        stat(
            "net-adr-future-days",
            "Net Adr (Future Days)",
            <StatsCard
                title="Net Adr (Future Days)"
                value={stats?.futureNetAdr || 0}
                lasPeriodValue={prev?.futureNetAdr || 0}
                lastYearValue={prevYear?.futureNetAdr || 0}
                periodDescription={period}
                CardIcon={ChartLineIcon}
                iconVariant="green"
                iconBgClassName="bg-green-200"
                className="h-full"
                dense
                tooltip={bookingPaceTooltips["Net Adr (Future Days)"]}
            />,
        ),
        // Cancellation row (decomposed, 5 cards) — same five as All Sources.
        ...cancellationTiles(sectionId, s, days, period),
    ];
};

export interface BookingPaceTilesResult {
    sections: SectionDef[];
    registry: TileDef[];
}

/**
 * Factory: from the per-section stats, return the generic `sections` + `registry`
 * for the dashboard grid. Section/tile ORDER is the contract — the Default
 * arrangement reproduces the current card order/grouping (FR-24,
 * dashboard-layout-editor).
 */
export const buildBookingPaceTiles = (input: BookingPaceTilesInput): BookingPaceTilesResult => {
    const sections: SectionDef[] = [];
    const registry: TileDef[] = [];

    const days: PeriodDays = {
        current: input.numberOfDays,
        prev: input.numberOfDaysPrev,
        prevYear: input.numberOfDaysPrevYear,
    };

    input.sectionsData.forEach((s, index) => {
        const sectionId = sectionIdForKey(s.key);
        const isAllSources = s.key === "All Sources";

        const tiles =
            isAllSources && index === 0
                ? allSourcesTiles(sectionId, input)
                : platformTiles(sectionId, s, days, input.period);

        registry.push(...tiles);
        sections.push({ id: sectionId, title: s.key, tileIds: tiles.map((t) => t.id) });
    });

    return { sections, registry };
};
